import mermaid from 'mermaid';

let currentTheme: 'light' | 'dark' = 'light';

// Read CSS custom properties (HSL components) and build hsl() strings.
function cssVar(name: string, fallback: string): string {
  if (typeof window === 'undefined') return fallback;
  const v = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
  return v ? `hsl(${v})` : fallback;
}

function buildThemeVariables() {
  const isDark = currentTheme === 'dark';
  // Soft, modern palette — gentle tints with good contrast in both themes.
  const p = isDark
    ? {
        bg: '#1e293b',
        node: '#312e81',
        nodeBorder: '#818cf8',
        text: '#e2e8f0',
        line: '#a5b4fc',
        secondary: '#0f766e',
        secondaryBorder: '#5eead4',
        tertiary: '#9d174d',
        tertiaryBorder: '#f9a8d4',
        note: '#78350f',
        noteBorder: '#fbbf24',
        noteText: '#fef3c7',
        actor: '#3730a3',
        actorBorder: '#a5b4fc',
      }
    : {
        bg: '#ffffff',
        node: '#eef2ff',
        nodeBorder: '#6366f1',
        text: '#1e293b',
        line: '#6366f1',
        secondary: '#ecfeff',
        secondaryBorder: '#06b6d4',
        tertiary: '#fdf2f8',
        tertiaryBorder: '#ec4899',
        note: '#fef3c7',
        noteBorder: '#f59e0b',
        noteText: '#78350f',
        actor: '#eef2ff',
        actorBorder: '#6366f1',
      };

  return {
    background: p.bg,
    primaryColor: p.node,
    primaryTextColor: p.text,
    primaryBorderColor: p.nodeBorder,
    lineColor: p.line,
    secondaryColor: p.secondary,
    secondaryBorderColor: p.secondaryBorder,
    secondaryTextColor: p.text,
    tertiaryColor: p.tertiary,
    tertiaryBorderColor: p.tertiaryBorder,
    tertiaryTextColor: p.text,
    // Sequence diagrams
    actorBkg: p.actor,
    actorBorder: p.actorBorder,
    actorTextColor: p.text,
    actorLineColor: p.nodeBorder,
    signalColor: p.text,
    signalTextColor: p.text,
    labelBoxBkgColor: p.node,
    labelBoxBorderColor: p.nodeBorder,
    labelTextColor: p.text,
    loopTextColor: p.text,
    noteBkgColor: p.note,
    noteBorderColor: p.noteBorder,
    noteTextColor: p.noteText,
    activationBkgColor: p.secondary,
    activationBorderColor: p.secondaryBorder,
    classText: p.text,
    // Gantt
    sectionBkgColor: p.secondary,
    sectionBkgColor2: p.tertiary,
    altSectionBkgColor: p.bg,
    taskBkgColor: p.nodeBorder,
    taskTextColor: '#ffffff',
    taskTextLightColor: p.text,
    taskTextOutsideColor: p.text,
    gridColor: p.nodeBorder,
    // Pie / charts
    pie1: '#6366f1',
    pie2: '#06b6d4',
    pie3: '#ec4899',
    pie4: '#f59e0b',
    pie5: '#10b981',
    pie6: '#8b5cf6',
    pie7: '#ef4444',
    pie8: '#14b8a6',
    pieTitleTextColor: p.text,
    pieSectionTextColor: '#ffffff',
    pieLegendTextColor: p.text,
    fontFamily: 'inherit',
  };
}

function init() {
  mermaid.initialize({
    startOnLoad: false,
    securityLevel: 'loose',
    fontFamily: 'inherit',
    theme: 'base',
    themeVariables: buildThemeVariables(),
    flowchart: { useMaxWidth: true, htmlLabels: true, curve: 'basis' },
    sequence: { useMaxWidth: true, wrap: true },
    gantt: { useMaxWidth: true },
    journey: { useMaxWidth: true },
    er: { useMaxWidth: true },
    pie: { useMaxWidth: true },
  });
}

// Run once on module load.
if (typeof window !== 'undefined') init();

export function setMermaidTheme(theme: 'light' | 'dark') {
  currentTheme = theme;
  init();
}

export function getMermaidTheme() {
  return currentTheme;
}

export async function renderMermaid(code: string, id: string): Promise<{ svg: string }> {
  // Trust mermaid to parse its own syntax for every diagram type.
  const { svg } = await mermaid.render(id, code.trim());
  return { svg };
}

export function detectDiagramType(code: string): string {
  const firstLine =
    code
      .split('\n')
      .map((l) => l.trim())
      .find((l) => l && !l.startsWith('%%')) || '';
  const match = firstLine.match(
    /^(flowchart|graph|sequenceDiagram|classDiagram|stateDiagram(?:-v2)?|erDiagram|gantt|pie|journey|mindmap|timeline|gitGraph|quadrantChart|requirementDiagram|C4Context|C4Container|C4Component|C4Dynamic|C4Deployment|sankey-beta|xychart-beta|block-beta)\b/i,
  );
  return match ? match[1] : 'diagram';
}
